import { Component, ChangeDetectionStrategy, signal, inject, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { HttpErrorResponse } from '@angular/common/http';
import { CustomerService, type CustomerDTO } from '../../services/customer.service';
import { PaymentService } from '../../services/payment.service';
import { AuthService } from '../../services/auth.service';
import { httpErrorApiMessage } from '../../core/api-response-utils';
import { SaleService, type SaleDTO } from '../../services/sale.service';
import { PaymentReadComponent } from './payment-read.component';
import { SaleReadComponent } from '../sales/sale-read.component';
import { CustomerReadComponent } from '../customers/customer-read.component';
import { SubscriptionReadComponent } from '../subscriptions/subscription-read.component';
import { SubscriptionService } from '../../services/subscription.service';
import { PaymentFormService } from '../../services/payment-form.service';
import {
  getPaymentFormLabel,
  getPaymentProviderLabel,
  PaymentRecordStatusEnum,
  type ChargeDTO,
  type ListPaymentFilterSpec,
  type PaymentFormDTO,
  type PaymentRecordDTO,
  type SubscriptionDTO
} from '../../core/models';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import { DrawerModule } from 'primeng/drawer';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { TooltipModule } from 'primeng/tooltip';
import { MenuModule } from 'primeng/menu';
import { Menu } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { ToastrService } from 'ngx-toastr';
import { formValueToApiDateTime, formValueToApiDateTimeEndExclusive } from '../../core/date-utils';

@Component({
  selector: 'app-payment-list',
  templateUrl: './payment-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        flex: 1 1 0%;
        min-height: 0;
        width: 100%;
      }
      :host ::ng-deep .payments-primeng-table .p-datatable-thead > tr > th {
        background: rgb(248 250 252);
        border-color: rgb(226 232 240);
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        padding: 0.4rem 0.45rem !important;
      }
      :host ::ng-deep .payments-primeng-table .p-datatable-tbody > tr > td {
        padding: 0.4rem 0.45rem !important;
      }
      :host ::ng-deep .payments-primeng-table .p-datatable-table {
        width: 100%;
        table-layout: auto;
      }
      :host ::ng-deep p-table {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
      }
      :host ::ng-deep .payments-primeng-table.p-datatable,
      :host ::ng-deep .p-datatable {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
      }
      :host ::ng-deep .p-datatable-table-container,
      :host ::ng-deep .p-datatable-flex-scrollable > .p-datatable-table-container {
        flex: 1 1 0;
        min-height: 0;
        overflow: auto;
      }
      :host ::ng-deep .p-datatable-paginator-bottom {
        flex-shrink: 0;
      }
      :host ::ng-deep .p-paginator .p-paginator-page.p-paginator-page-selected,
      :host ::ng-deep button.p-paginator-page-selected {
        background: rgb(224 242 254) !important;
        border-color: rgb(14 165 233) !important;
        color: rgb(2 132 199) !important;
      }
      :host ::ng-deep .sale-details-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .sale-details-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .customer-details-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .customer-details-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .subscription-details-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .subscription-details-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
    `
  ],
  imports: [
    CommonModule,
    MatIconModule,
    ReactiveFormsModule,
    FormsModule,
    PaymentReadComponent,
    SaleReadComponent,
    CustomerReadComponent,
    SubscriptionReadComponent,
    TableModule,
    ButtonModule,
    SkeletonModule,
    FloatLabelModule,
    InputTextModule,
    TagModule,
    DrawerModule,
    DatePickerModule,
    SelectModule,
    IconFieldModule,
    InputIconModule,
    TooltipModule,
    MenuModule
  ]
})
export class PaymentListComponent {
  private paymentService = inject(PaymentService);
  private paymentFormService = inject(PaymentFormService);
  private saleService = inject(SaleService);
  private customerService = inject(CustomerService);
  private subscriptionService = inject(SubscriptionService);
  private authService = inject(AuthService);
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private toastr = inject(ToastrService);

  readonly canExportPayments = this.authService.hasRole('PAYMENT_EXPORT');
  exporting = signal(false);

  get exportButtonTooltip(): string {
    if (!this.canExportPayments) {
      return 'No tienes permiso para descargar este listado. Pide acceso a un administrador.';
    }
    if (this.totalItems() === 0) {
      return 'No hay pagos para exportar con los filtros actuales';
    }
    return 'Descargar todos los pagos filtrados en CSV';
  }

  @ViewChild('rowActionsMenu') rowActionsMenu?: Menu;

  selectedPayment = signal<PaymentRecordDTO | null>(null);
  isDetailsOpen = signal(false);
  detailsLoading = signal(false);
  selectedSale = signal<SaleDTO | null>(null);
  saleCharges = signal<ChargeDTO[]>([]);
  isSaleDetailsOpen = signal(false);
  saleDetailsLoading = signal(false);

  selectedCustomer = signal<CustomerDTO | null>(null);
  isCustomerDetailsOpen = signal(false);
  customerDetailsLoading = signal(false);

  selectedSubscription = signal<SubscriptionDTO | null>(null);
  isSubscriptionDetailsOpen = signal(false);
  subscriptionDetailsLoading = signal(false);

  rowMenuItems = signal<MenuItem[]>([]);

  payments = signal<PaymentRecordDTO[]>([]);
  loading = signal(false);
  initialLoadDone = signal(false);
  error = signal<string | null>(null);
  currentPage = signal(0);
  pageSize = signal(20);
  totalItems = signal(0);
  totalPages = signal(0);
  pageSizeOptions = [20, 50, 100];
  skeletonRows = [1, 2, 3, 4, 5, 6, 7, 8];
  hasActiveFilters = signal(false);

  readonly getPaymentFormLabel = getPaymentFormLabel;
  readonly getPaymentProviderLabel = getPaymentProviderLabel;
  paymentFormOptions = signal<PaymentFormDTO[]>([]);

  private readonly locale = typeof navigator !== 'undefined' ? navigator.language : undefined;
  /** Descarta respuestas de listados anteriores cuando hay una carga más reciente. */
  private loadGeneration = 0;

  filtersForm = this.fb.group({
    q: [''],
    saleNumber: [''],
    paymentFormId: [undefined as number | undefined],
    paidAtRange: this.fb.control<Date[] | null>(null)
  });

  constructor() {
    this.filtersForm.controls.q.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntilDestroyed()
    ).subscribe(() => this.applyFilters());

    this.filtersForm.controls.saleNumber.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntilDestroyed()
    ).subscribe(() => this.applyFilters());

    this.filtersForm.controls.paymentFormId.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.applyFilters());

    this.filtersForm.controls.paidAtRange.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.applyFilters());

    this.loadPaymentForms();
    this.loadPayments();
    const paymentId = Number(this.route.snapshot.queryParamMap.get('id'));
    if (paymentId) {
      this.openPaymentDetails({ id: paymentId } as PaymentRecordDTO);
    }
  }

  loadPaymentForms(): void {
    this.paymentFormService
      .listPaymentForms({ state: 'ACTIVE' }, { page: 0, size: 500 })
      .pipe(catchError(() => of({ ok: false, body: [] as PaymentFormDTO[] })))
      .subscribe((r) => {
        this.paymentFormOptions.set(r.ok && r.body ? r.body : []);
      });
  }

  loadPayments(): void {
    const generation = ++this.loadGeneration;
    this.loading.set(true);
    this.error.set(null);

    const spec = this.buildSpecFromFilters();
    const pageable = { page: this.currentPage(), size: this.pageSize(), sort: ['paidAt,desc'] };

    this.paymentService.listPayments(spec, pageable).subscribe({
      next: (response) => {
        if (generation !== this.loadGeneration) return;
        if (response.ok && response.body) {
          this.payments.set(response.body);
          if (response.page) {
            this.totalItems.set(response.page.totalItems ?? 0);
            this.totalPages.set(response.page.totalPages ?? 0);
            this.pageSize.set(response.page.pageSize ?? this.pageSize());
            this.currentPage.set(Math.max(0, (response.page.currentPage ?? 1) - 1));
          } else {
            this.totalItems.set(response.body.length);
            this.totalPages.set(Math.ceil(this.totalItems() / this.pageSize()));
          }
        } else {
          this.error.set(response.message || 'Error al cargar pagos');
        }
        this.loading.set(false);
        this.initialLoadDone.set(true);
      },
      error: (err) => {
        if (generation !== this.loadGeneration) return;
        this.error.set('Error al conectar con el servidor. Verifica que el API esté ejecutándose.');
        this.loading.set(false);
        this.initialLoadDone.set(true);
        console.error('Error loading payments:', err);
      }
    });
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    const rows = event.rows || this.pageSize();
    const page = rows ? Math.floor((event.first ?? 0) / rows) : 0;
    if (page === this.currentPage() && rows === this.pageSize()) return;
    this.currentPage.set(page);
    this.pageSize.set(rows);
    this.loadPayments();
  }

  getCustomerName(payment: PaymentRecordDTO): string {
    const customer = payment.customer;
    if (!customer) return '-';
    return customer.comercialName || customer.legalName || customer.taxId || '-';
  }

  refreshTable(): void {
    this.loadPayments();
  }

  exportCsv(): void {
    if (!this.canExportPayments || this.exporting()) return;
    this.exporting.set(true);
    this.paymentService.exportPaymentsCsv(this.buildSpecFromFilters(), ['paidAt,desc']).subscribe({
      next: ({ blob, filename }) => {
        this.triggerBrowserDownload(blob, filename);
        this.exporting.set(false);
        this.toastr.success('CSV de pagos descargado');
      },
      error: (err: HttpErrorResponse) => {
        this.exporting.set(false);
        const fallback = 'No se pudo exportar el CSV de pagos';
        if (err.error instanceof Blob) {
          err.error.text().then((text) => {
            try {
              const json = JSON.parse(text) as { message?: string };
              this.toastr.error(json?.message?.trim() || fallback);
            } catch {
              this.toastr.error(fallback);
            }
          });
          return;
        }
        this.toastr.error(httpErrorApiMessage(err, fallback));
        console.error('Error exporting payments CSV:', err);
      }
    });
  }

  private triggerBrowserDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'pagos.csv';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  applyFilters(): void {
    this.syncActiveFilters();
    this.currentPage.set(0);
    this.loadPayments();
  }

  clearFilters(): void {
    this.filtersForm.reset(
      {
        q: '',
        saleNumber: '',
        paymentFormId: undefined,
        paidAtRange: null
      },
      { emitEvent: false }
    );
    this.applyFilters();
  }

  private syncActiveFilters(): void {
    const raw = this.filtersForm.getRawValue();
    this.hasActiveFilters.set(
      !!(
        raw.q?.trim() ||
        raw.saleNumber?.trim() ||
        raw.paymentFormId ||
        this.resolvePaidAtRange(raw.paidAtRange)
      )
    );
  }

  openPaymentDetails(payment: PaymentRecordDTO): void {
    if (!payment?.id) return;
    this.isDetailsOpen.set(true);
    this.selectedPayment.set(null);
    this.detailsLoading.set(true);
    this.paymentService.getPaymentById(payment.id).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          this.selectedPayment.set(response.body);
        } else {
          this.error.set(response.message || 'Error al cargar el pago');
        }
        this.detailsLoading.set(false);
      },
      error: (err) => {
        this.error.set('Error al cargar el pago. Verifica que el API esté ejecutándose.');
        this.detailsLoading.set(false);
        console.error('Error loading payment by id:', err);
      }
    });
  }

  closeDetails(): void {
    this.isDetailsOpen.set(false);
    this.selectedPayment.set(null);
    this.detailsLoading.set(false);
  }

  onDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeDetails();
  }

  openSaleDetails(saleId: number, event?: Event): void {
    event?.stopPropagation();
    if (!saleId) return;
    this.isSaleDetailsOpen.set(true);
    this.selectedSale.set(null);
    this.saleCharges.set([]);
    this.saleDetailsLoading.set(true);
    this.saleService.getSaleById(saleId).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          const detail = response.body;
          this.selectedSale.set(detail);
          this.saleService.getSaleCharges(detail.id!).subscribe({
            next: (chargesResp) => {
              this.saleCharges.set(chargesResp.body ?? []);
            },
            error: () => this.saleCharges.set([])
          });
        } else {
          this.closeSaleDetails();
        }
        this.saleDetailsLoading.set(false);
      },
      error: (err) => {
        this.saleDetailsLoading.set(false);
        this.closeSaleDetails();
        console.error('Error loading sale detail:', err);
      }
    });
  }

  closeSaleDetails(): void {
    this.isSaleDetailsOpen.set(false);
    this.selectedSale.set(null);
    this.saleCharges.set([]);
    this.saleDetailsLoading.set(false);
  }

  onSaleDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeSaleDetails();
  }

  editSaleFromModal(): void {
    const sale = this.selectedSale();
    if (!sale?.id) return;
    this.closeSaleDetails();
    this.router.navigate(['/sales', sale.id]);
  }

  openCustomerDetails(customerId: number): void {
    if (!customerId) return;
    this.isCustomerDetailsOpen.set(true);
    this.selectedCustomer.set(null);
    this.customerDetailsLoading.set(true);
    this.customerService.getCustomerById(customerId).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          this.selectedCustomer.set(response.body);
        } else {
          this.closeCustomerDetails();
        }
        this.customerDetailsLoading.set(false);
      },
      error: (err) => {
        this.customerDetailsLoading.set(false);
        this.closeCustomerDetails();
        console.error('Error loading customer by id:', err);
      }
    });
  }

  closeCustomerDetails(): void {
    this.isCustomerDetailsOpen.set(false);
    this.selectedCustomer.set(null);
    this.customerDetailsLoading.set(false);
  }

  onCustomerDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeCustomerDetails();
  }

  editCustomerFromModal(): void {
    const customer = this.selectedCustomer();
    if (!customer?.id) return;
    this.closeCustomerDetails();
    this.router.navigate(['/customers', customer.id]);
  }

  openSaleDetailsFromCustomer(saleId: number): void {
    this.openSaleDetails(saleId);
  }

  openSubscriptionDetails(subscriptionId: number): void {
    if (!subscriptionId) return;
    this.isSubscriptionDetailsOpen.set(true);
    this.selectedSubscription.set(null);
    this.subscriptionDetailsLoading.set(true);
    this.subscriptionService.getSubscriptionById(subscriptionId).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          this.selectedSubscription.set(response.body);
        } else {
          this.toastr.error(response.message || 'No se pudo cargar la suscripción');
          this.closeSubscriptionDetails();
        }
        this.subscriptionDetailsLoading.set(false);
      },
      error: (err) => {
        this.subscriptionDetailsLoading.set(false);
        this.toastr.error('Error al cargar la suscripción');
        this.closeSubscriptionDetails();
        console.error('Error loading subscription by id:', err);
      }
    });
  }

  closeSubscriptionDetails(): void {
    this.isSubscriptionDetailsOpen.set(false);
    this.selectedSubscription.set(null);
    this.subscriptionDetailsLoading.set(false);
  }

  onSubscriptionDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeSubscriptionDetails();
  }

  editSubscriptionFromModal(): void {
    const subscription = this.selectedSubscription();
    if (!subscription?.id) return;
    this.closeSubscriptionDetails();
    this.router.navigate(['/subscriptions', subscription.id]);
  }

  openRowMenu(event: Event, payment: PaymentRecordDTO): void {
    event.stopPropagation();
    this.rowMenuItems.set([
      {
        label: 'Ver',
        icon: 'pi pi-eye',
        command: () => this.openPaymentDetails(payment)
      }
    ]);
    this.rowActionsMenu?.toggle(event);
  }

  formatAmount(amount: number | undefined | null): string {
    if (amount == null) return '-';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }

  formatDateTime(value: string | undefined | null): string {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString(this.locale);
  }

  private buildSpecFromFilters(): ListPaymentFilterSpec {
    const value = this.filtersForm.getRawValue();
    const spec: ListPaymentFilterSpec = {
      status: PaymentRecordStatusEnum.PAID
    };

    if (value.q?.trim()) {
      spec['q[lk]'] = value.q.trim();
    }
    if (value.saleNumber?.trim()) {
      spec['saleNumber[lk]'] = value.saleNumber.trim();
    }
    if (value.paymentFormId) {
      spec.paymentFormId = value.paymentFormId;
    }
    const paidAtRange = this.resolvePaidAtRange(value.paidAtRange);
    if (paidAtRange) {
      const [fromRaw, toRaw] = paidAtRange;
      const from = formValueToApiDateTime(fromRaw);
      const toExclusive = formValueToApiDateTimeEndExclusive(toRaw);
      if (from) spec['paidAt[gte]'] = from;
      if (toExclusive) spec['paidAt[lt]'] = toExclusive;
    }

    return spec;
  }

  /**
   * Rango efectivo para el API.
   * Si solo hay fecha inicio (datepicker aún abierto), filtra ese día completo.
   */
  private resolvePaidAtRange(range: Date[] | null | undefined): [Date, Date] | null {
    if (!range || !Array.isArray(range) || !range[0]) return null;
    return [range[0], range[1] ?? range[0]];
  }
}
